import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { extensionOf, preparePhoto } from "../helpers/uploads/photos";

let nextId = 0;

const splitName = (fileName) => {
  const text = String(fileName ?? "");
  const dot = text.lastIndexOf(".");
  return dot === -1
    ? { name: text, extension: "" }
    : { name: text.slice(0, dot), extension: text.slice(dot) };
};

/**
 * Files picked for an upload. Each file is made ready in the background as
 * soon as it's picked (photos are shrunk, see helpers/uploads/photos), so the
 * upload itself starts small and right away.
 *
 * - `items`: [{ id, file, name, extension, preparing }] for the list on
 *   screen; `name` is the file name without its extension (editable), `file`
 *   is the file as it will go (the shrunk photo once it's ready).
 * - `add(files)`: a FileList or array. Files without an extension are skipped
 *   (the backend names stored files by it), unless `needsExtension: false`.
 * - `rename(index, name)`, `remove(index)`, `clear()`.
 * - `preparing`: true while any file is still being made ready.
 * - `ready()`: waits for every file and gives [{ file, filename }] in order,
 *   for appendFiles(formData, …).
 *
 * `prepare(file)` (default preparePhoto) makes one file ready; it may return
 * the file itself.
 */
export default function usePickedFiles({
  prepare = preparePhoto,
  needsExtension = true,
} = {}) {
  const [items, setItems] = useState([]);
  const latest = useRef(items);
  latest.current = items;
  const jobs = useRef(new Map());
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const add = useCallback(
    (picked) => {
      const added = Array.from(picked ?? [])
        .filter(
          (file) => file && (!needsExtension || splitName(file.name).extension),
        )
        .map((file) => ({
          id: `picked-${(nextId += 1)}`,
          file,
          original: file,
          ...splitName(file.name),
          preparing: true,
        }));
      if (!added.length) return;
      setItems((current) => [...current, ...added]);
      added.forEach((item) => {
        let started;
        try {
          started = Promise.resolve(prepare(item.original));
        } catch {
          started = Promise.resolve(item.original);
        }
        const job = started
          .catch(() => item.original)
          .then((file) => file ?? item.original);
        jobs.current.set(item.id, job);
        job.then((file) => {
          if (!live.current) return;
          setItems((current) =>
            current.map((entry) =>
              entry.id === item.id
                ? { ...entry, file, preparing: false }
                : entry,
            ),
          );
        });
      });
    },
    [prepare, needsExtension],
  );

  const rename = useCallback(
    (index, name) =>
      setItems((current) =>
        current.map((item, at) => (at === index ? { ...item, name } : item)),
      ),
    [],
  );

  const remove = useCallback((index) => {
    const gone = latest.current[index];
    if (!gone) return;
    jobs.current.delete(gone.id);
    setItems((current) => current.filter((item) => item.id !== gone.id));
  }, []);

  const clear = useCallback(() => {
    jobs.current.clear();
    setItems([]);
  }, []);

  const ready = useCallback(async () => {
    const current = latest.current;
    const files = await Promise.all(
      current.map((item) => jobs.current.get(item.id) ?? item.file),
    );
    return current.map((item, index) => {
      const file = files[index];
      const extension =
        file === item.original ? item.extension : extensionOf(file?.name);
      return { file, filename: `${item.name}${extension}` };
    });
  }, []);

  const preparing = items.some((item) => item.preparing);
  return useMemo(
    () => ({ items, add, rename, remove, clear, ready, preparing }),
    [items, add, rename, remove, clear, ready, preparing],
  );
}
